import { Injectable, signal } from "@angular/core";

export const MAX_PINNED_TOOLS = 4;
const ORDER_KEY = "plugins.toolOrder";
const PINS_KEY = "plugins.toolPins";
const OPENS_KEY = "plugins.toolOpens";
const OPENS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable/full: the change still applies for this session.
  }
}

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === "string"))] : [];

const openTimes = (v: unknown): Record<string, number[]> => {
  const result: Record<string, number[]> = {};
  if (v && typeof v === "object" && !Array.isArray(v)) {
    for (const [id, times] of Object.entries(v)) {
      if (Array.isArray(times)) {
        result[id] = times.filter((t): t is number => typeof t === "number" && isFinite(t));
      }
    }
  }
  return result;
};

/**
 * How the user has arranged "Your tools": up to {@link MAX_PINNED_TOOLS} pinned tools that always
 * come first, a manual (drag) order, and how often each tool was opened in the last 30 days.
 * Shared by the Tools & Plugins page and its "Edit your tools" panel so both stay in step.
 */
@Injectable({ providedIn: "root" })
export class ToolPrefsService {
  /** Set by {@link useUser}; keeps one user's arrangement from showing up for another. */
  private userId?: string;

  /** Pinned tool ids, in the order the user set. */
  readonly pins = signal<string[]>(strings(read<unknown>(PINS_KEY, [])));
  /** Manual order from dragging; empty until the user drags (then it wins over "most used"). */
  readonly order = signal<string[]>(strings(read<unknown>(ORDER_KEY, [])));
  private readonly opens = signal<Record<string, number[]>>(
    openTimes(read<unknown>(OPENS_KEY, {})),
  );

  /** Switches to the signed-in user's saved arrangement. Call once the user id is known. */
  useUser(userId: string | undefined): void {
    this.userId = userId;
    this.pins.set(strings(read<unknown>(this.key(PINS_KEY), [])));
    this.order.set(strings(read<unknown>(this.key(ORDER_KEY), [])));
    this.opens.set(openTimes(read<unknown>(this.key(OPENS_KEY), {})));
  }

  get isFull(): boolean {
    return this.pins().length >= MAX_PINNED_TOOLS;
  }

  pin(id: string): void {
    if (this.isFull || this.pins().includes(id)) {
      return;
    }
    this.setPins([...this.pins(), id]);
  }

  unpin(id: string): void {
    this.setPins(this.pins().filter((p) => p !== id));
  }

  setPins(ids: string[]): void {
    const next = [...new Set(ids)].slice(0, MAX_PINNED_TOOLS);
    this.pins.set(next);
    write(this.key(PINS_KEY), next);
  }

  /**
   * Drops pins for tools that are gone (e.g. uninstalled) so they stop using up the pin slots.
   * Call with the ids of the loaded tools, not while the list is still loading.
   */
  prunePins(availableIds: string[]): void {
    const available = new Set(availableIds);
    const next = this.pins().filter((id) => available.has(id));
    if (next.length !== this.pins().length) {
      this.setPins(next);
    }
  }

  setOrder(ids: string[]): void {
    const next = [...new Set(ids)];
    this.order.set(next);
    write(this.key(ORDER_KEY), next);
  }

  recordOpen(id: string): void {
    const now = Date.now();
    const cutoff = now - OPENS_WINDOW_MS;
    const next: Record<string, number[]> = {};
    for (const [toolId, times] of Object.entries(this.opens())) {
      const recent = times.filter((t) => t >= cutoff);
      if (recent.length) {
        next[toolId] = recent;
      }
    }
    next[id] = [...(next[id] ?? []), now];
    this.opens.set(next);
    write(this.key(OPENS_KEY), next);
  }

  /** Opens in the last 30 days. */
  opensOf(id: string): number {
    const cutoff = Date.now() - OPENS_WINDOW_MS;
    return (this.opens()[id] ?? []).filter((t) => t >= cutoff).length;
  }

  /**
   * Pinned tools first (in pin order), then everything else: the user's dragged order if they
   * have one, otherwise most opened first. Ties keep the input order.
   */
  arrange<T extends { id: string }>(tools: T[]): T[] {
    const pins = this.pins();
    const manual = this.order();
    const pinned = pins.map((id) => tools.find((t) => t.id === id)).filter((t): t is T => !!t);
    const rest = tools.filter((t) => !pins.includes(t.id));
    const rank = (id: string) => {
      const i = manual.indexOf(id);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const sorted = manual.length
      ? [...rest].sort((a, b) => rank(a.id) - rank(b.id))
      : [...rest].sort((a, b) => this.opensOf(b.id) - this.opensOf(a.id));
    return [...pinned, ...sorted];
  }

  private key(base: string): string {
    return this.userId ? `${base}.${this.userId}` : base;
  }
}
